// Campaign plan editor. The prototype layout is retained while all persisted
// data, estimates, forecasts, and state transitions come from the API.
//
// The state lives in `useCreateFlow` (create-flow-state.ts), the save path in
// `campaignPlanSave` and the lifecycle in `campaignFlowActions`; each step of the
// wizard renders from the one model they make.

import { ActionToast } from '@/components/action-toast';
import { ResponsivePicker } from '@/components/responsive-picker';
import { BuilderSummary } from '@/components/builder-summary';
import { AudienceSyncGate } from '@/components/audience-sync-gate';
import { CreativeSyncGate } from '@/components/creative-sync-gate';
import { ForecastPanel } from './create-forecast-panel';
import { CampaignPublishingModal } from './create-publishing-modal';
import { CampaignLaunchDialog } from './create-launch-dialog';
import { ExtendFlightDialog } from '@/components/extend-flight-dialog';
import { useCreateFlow, type CreateFlowProps } from './create-flow-state';
import { campaignPlanSave } from './create-flow-save';
import { campaignFlowActions } from './create-flow-actions';
import type { CreateFlowModel } from './create-flow-model';
import { CreateFlowHeader } from './create-flow-header';
import { ChannelStep, ProductionStep } from './create-step-setup';
import { AudienceStep } from './create-step-audience';
import { CreativeStep, PreviewStep } from './create-step-creative';
import { ScheduleStep } from './create-step-schedule';
import { ReviewStep } from './create-step-review';

export function CreateFlow(props: CreateFlowProps) {
  const flow = useCreateFlow(props);
  const model: CreateFlowModel = { ...flow, ...campaignPlanSave(flow), ...campaignFlowActions(flow) };
  const { production, channel, startDate, endDate, name, setName, savedName,
    renaming, setRenaming, audienceSlots, markup, flights, setActionNotice, required,
    savedCampaignId, audienceTypeSummary, undoToast, setUndoToast, launchOpen, publishProgress,
    audienceSyncGate, setAudienceSyncGate, creativeSyncGate, setCreativeSyncGate,
    campaignSyncQuery, publishSteps, publishSettled, deliveryProps, publishReadUnavailable,
    freshCampaignSyncStatus, forecastRequest, forecastQuery, forecastNote, publishLogRequest,
    undoStatus, handleAudienceSyncComplete, handleCreativeSyncComplete, handleLaunchWhenReady,
    handleMarkReady, handleLaunchDialogDismiss, handlePublishingDone,
    handlePublishingFailureDismiss, commitName, extendOpen, setExtendOpen, extensionSignal,
    handleExtended, publishTarget, currentRequestedStatus, lastFlight, todayStr, maxEndISO, plan,
    campaign,
  } = model;

  return (
    <div>
      <CreateFlowHeader model={model} />

      <div className="create-wrap">
        <div className="create-main">
          <ProductionStep model={model} />
          <ChannelStep model={model} />
          <AudienceStep model={model} />
          <CreativeStep model={model} />
          <PreviewStep model={model} />
          <ScheduleStep model={model} />
          <ReviewStep model={model} />
        </div>

        <BuilderSummary className="create-side">
          <ForecastPanel
            production={production}
            channel={channel}
            forecastQuery={forecastQuery}
            forecastRequest={forecastRequest}
            forecastNote={forecastNote}
            flights={flights}
            publishSteps={publishSteps}
            publishReadUnavailable={publishReadUnavailable}
            publishSettled={publishSettled}
            delivery={deliveryProps}
            openRequest={publishLogRequest}
          />
        </BuilderSummary>
      </div>

      {renaming && (
        <ResponsivePicker kind="confirmation" label="Rename campaign" onClose={() => setRenaming(false)}>
          <div className="campaign-rename-sheet">
            <label className="field-lbl" htmlFor="campaign-name-sheet">Campaign name</label>
            <input
              id="campaign-name-sheet"
              className="input"
              value={name}
              autoFocus
              onChange={(event) => {
                setName(event.target.value);
                if (event.target.value.trim()) required.clear('campaign-name');
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter') { event.preventDefault(); setRenaming(false); void commitName(); }
              }}
            />
            <div className="campaign-rename-actions">
              <button type="button" className="btn btn-primary" onClick={() => { setRenaming(false); void commitName(); }}>Save name</button>
              <button type="button" className="btn btn-ghost" onClick={() => { setName(savedName); setRenaming(false); }}>Cancel</button>
            </div>
          </div>
        </ResponsivePicker>
      )}

      {/* Confirm the immutable handoff before queuing the provider destination. */}
      {launchOpen && (
        <CampaignLaunchDialog
          campaignId={savedCampaignId}
          campaignName={name}
          channel={channel}
          audienceTypeSummary={audienceTypeSummary}
          audienceSlotCount={audienceSlots.length}
          markup={markup}
          startDate={startDate}
          endDate={endDate}
          onConfirm={handleMarkReady}
          onDismiss={handleLaunchDialogDismiss}
        />
      )}

      {/* Extend flight (campaign lifecycle 2b, 2026-10-21): the form, or the request in flight. */}
      {extendOpen && savedCampaignId && (
        <ExtendFlightDialog
          campaignId={savedCampaignId}
          campaignName={name}
          revision={plan?.revision ?? campaign?.revision ?? 1}
          target={publishTarget}
          paused={currentRequestedStatus === 'paused'}
          lastFlightEnd={lastFlight?.end ?? endDate}
          suggestedBudget={lastFlight ? +lastFlight.budget || null : null}
          todayNy={todayStr}
          latestEnd={maxEndISO}
          current={extensionSignal?.row ?? null}
          onClose={() => setExtendOpen(false)}
          onExtended={handleExtended}
        />
      )}

      {publishProgress && (
        <CampaignPublishingModal
          publishSteps={publishSteps}
          campaignName={name}
          target={publishProgress.target}
          snapshot={freshCampaignSyncStatus}
          statusCheckFailed={campaignSyncQuery.isError}
          refreshing={campaignSyncQuery.isFetching}
          onDone={handlePublishingDone}
          onRetry={() => { void campaignSyncQuery.refetch(); }}
          onDismissFailure={handlePublishingFailureDismiss}
        />
      )}

      {audienceSyncGate && (
        <AudienceSyncGate
          items={audienceSyncGate.items}
          onSynced={handleAudienceSyncComplete}
          onReturnToDraft={() => {
            setAudienceSyncGate(null);
            setActionNotice(
              'Campaign remains a draft. Publishing continues in the background; retry pre-flight when the audiences are ready.',
            );
          }}
          onLaunchWhenReady={audienceSyncGate.campaignId
            ? () => handleLaunchWhenReady(audienceSyncGate.campaignId)
            : undefined}
        />
      )}

      {creativeSyncGate && (
        <CreativeSyncGate
          items={creativeSyncGate.items}
          onSynced={handleCreativeSyncComplete}
          onDismiss={() => {
            setCreativeSyncGate(null);
            setActionNotice(
              'Campaign remains a draft. Publishing continues in the background; retry pre-flight after every creative is published.',
            );
          }}
          onLaunchWhenReady={creativeSyncGate.campaignId
            ? () => handleLaunchWhenReady(creativeSyncGate.campaignId)
            : undefined}
        />
      )}

      {/* Campaign pause/resume toast: announced, with Undo, 7 s auto-dismiss. */}
      <ActionToast
        toast={undoToast ? {
          key: undoToast.key,
          message: <>Campaign <b>{undoToast.to === 'paused' ? 'paused' : 'resumed'}</b>{undoToast.to === 'paused' ? ' — delivery stops within ~5 min.' : ' — delivery starts within ~5 min.'}</>,
          action: { label: 'Undo', onClick: undoStatus },
        } : null}
        onDismiss={() => setUndoToast(null)}
      />
    </div>
  );
}
